import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  browserTimeZone,
  formatRangeLabel,
  parseRangeParams,
  previousPeriodLabel,
  rangeToParams,
  resolveRange,
  type RangeSelection,
} from "@/lib/dashboardDateRange";
import {
  formatDuration,
  formatInt,
  formatLatency,
  formatMinutes,
  formatPercent,
  formatUsd,
  ratio,
} from "@/lib/statsFormat";
import {
  fetchCallDashboardStats,
  type CallDashboardStats,
} from "@/services/callStatsService";
import { useAuth } from "../../../supabase/auth";
import DashboardLayout from "../dashboard/layout/DashboardLayout";
import PageHeader from "../dashboard/layout/PageHeader";
import AssistantTable from "../dashboard/stats/AssistantTable";
import BusyHoursHeatmap from "../dashboard/stats/BusyHoursHeatmap";
import CallLengthChart from "../dashboard/stats/CallLengthChart";
import ConversationQuality from "../dashboard/stats/ConversationQuality";
import OutcomeChart from "../dashboard/stats/OutcomeChart";
import CostBreakdown from "../dashboard/stats/CostBreakdown";
import DateRangeFilter from "../dashboard/stats/DateRangeFilter";
import EndedReasonChart from "../dashboard/stats/EndedReasonChart";
import PickupStat from "../dashboard/stats/PickupStat";
import ProviderTable from "../dashboard/stats/ProviderTable";
import StatCard from "../dashboard/stats/StatCard";
import TrendChart from "../dashboard/stats/TrendChart";

const tz = browserTimeZone();

const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<CallDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const paramsKey = searchParams.toString();
  const selection = useMemo(
    () => parseRangeParams(new URLSearchParams(paramsKey)),
    [paramsKey],
  );
  const range = useMemo(
    () => resolveRange(selection.preset, selection.customFrom, selection.customTo),
    [selection],
  );
  const { from, to } = range;

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCallDashboardStats(
        range.from,
        range.to,
        tz,
        range.prevFrom,
        range.prevTo,
      );
      if (requestId === latestRequest.current) setStats(result);
    } catch (err) {
      if (requestId === latestRequest.current) {
        setError(err instanceof Error ? err.message : "Failed to load stats");
      }
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const handleRangeChange = (next: RangeSelection) => {
    setSearchParams(rangeToParams(next), { replace: true });
  };

  const comparison = previousPeriodLabel(selection.preset, range.days);
  const current = stats?.totals.current;
  const previous = stats?.totals.previous;

  const avgLength = (t: typeof current) =>
    t && t.connected > 0 ? t.seconds / t.connected : null;
  const connectRate = (t: typeof current) => (t ? ratio(t.connected, t.calls) : null);
  const costPerMinute = (t: typeof current) =>
    t && t.seconds > 0 ? t.cost_total / (t.seconds / 60) : null;

  const cards = current && previous && [
    {
      title: "Total calls",
      value: formatInt(current.calls),
      current: current.calls,
      previous: previous.calls,
      goodWhen: "neutral" as const,
    },
    {
      title: "Connect rate",
      value: formatPercent(connectRate(current)),
      subLine: `${formatInt(current.connected)} connected`,
      current: connectRate(current),
      previous: connectRate(previous),
      goodWhen: "up" as const,
    },
    {
      title: "Total minutes",
      value: formatMinutes(current.seconds),
      current: current.seconds,
      previous: previous.seconds,
      goodWhen: "neutral" as const,
    },
    {
      title: "Avg call length",
      value: formatDuration(avgLength(current)),
      current: avgLength(current),
      previous: avgLength(previous),
      goodWhen: "neutral" as const,
    },
    {
      title: "Total cost",
      value: formatUsd(current.cost_total),
      subLine: `${formatUsd(costPerMinute(current), true)} / min`,
      current: current.cost_total,
      previous: previous.cost_total,
      goodWhen: "down" as const,
    },
    {
      title: "Avg latency",
      value: formatLatency(current.latency_avg_ms),
      subLine: `p95 ${formatLatency(current.latency_p95_ms)}`,
      current: current.latency_avg_ms,
      previous: previous.latency_avg_ms,
      goodWhen: "down" as const,
    },
    {
      title: "Errors",
      value: formatInt(current.errors),
      subLine: formatPercent(ratio(current.errors, current.calls)),
      current: current.errors,
      previous: previous.errors,
      goodWhen: "down" as const,
    },
    {
      title: "Reschedules",
      value: formatInt(current.reschedules),
      subLine: `${formatInt(current.tts_failovers)} TTS failovers`,
      current: current.reschedules,
      previous: previous.reschedules,
      goodWhen: "neutral" as const,
    },
  ];

  return (
    <DashboardLayout activeItem="Dashboard">
      <div className="space-y-6">
        <PageHeader
          title="Dashboard"
          description="Call volume, duration, cost and quality for your organization"
        />

        <div className="space-y-2 px-4 sm:px-6">
          <DateRangeFilter
            preset={selection.preset}
            from={from}
            to={to}
            onChange={handleRangeChange}
          />
          <p className="text-xs text-muted-foreground">
            {formatRangeLabel(selection.preset, from, to, tz)}
          </p>
        </div>

        <div className="space-y-6 px-4 pb-6 sm:px-6">
          {error && (
            <Card>
              <CardContent className="flex items-center justify-between gap-4">
                <p className="text-sm text-danger">{error}</p>
                <Button size="sm" variant="outline" onClick={load}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}

          {!stats && loading && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 8 }, (_, index) => (
                <Skeleton key={index} className="h-24" />
              ))}
            </div>
          )}

          {stats && cards && (
            <div
              className={cn(
                "space-y-6 transition-opacity",
                loading && "opacity-60",
              )}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {cards.map((card) => (
                  <StatCard key={card.title} comparisonLabel={comparison} {...card} />
                ))}
              </div>

              {current.calls === 0 ? (
                <Card>
                  <CardContent className="text-center text-sm text-muted-foreground">
                    No calls in this range
                  </CardContent>
                </Card>
              ) : (
                <>
                  <TrendChart
                    stats={stats}
                    fromFirstCall={selection.preset === "all"}
                  />
                  <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
                    <div className="space-y-6">
                      <CostBreakdown totals={current} />
                      {stats.by_channel && stats.ended_reasons_by_channel && (
                        <PickupStat
                          channels={stats.by_channel}
                          reasons={stats.ended_reasons_by_channel}
                          causes={stats.hangup_causes}
                        />
                      )}
                    </div>
                    <div className="space-y-6">
                      <EndedReasonChart
                        rows={stats.ended_reasons}
                        rowsByChannel={stats.ended_reasons_by_channel}
                      />
                      {stats.outcomes && <OutcomeChart rows={stats.outcomes} />}
                    </div>
                  </div>
                  {(current.turns !== undefined || stats.duration_buckets) && (
                    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
                      {current.turns !== undefined && (
                        <ConversationQuality totals={current} />
                      )}
                      {stats.duration_buckets && (
                        <CallLengthChart rows={stats.duration_buckets} />
                      )}
                    </div>
                  )}
                  <AssistantTable rows={stats.by_assistant} />
                  <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
                    <ProviderTable rows={stats.by_provider} />
                    <BusyHoursHeatmap cells={stats.heatmap} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
